/**
 * Session state: who is logged in, which company, what they may do.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react'
import { clearTokens, getAccessToken, onAuthChange } from '../api/client'
import { queryClient } from '../api/queryClient'
import {
  fetchMe, login as apiLogin, logout as apiLogout, switchCompanyApi,
} from '../api/endpoints'
import type { Me } from '../api/types'

interface AuthValue {
  me: Me | null
  loading: boolean
  isAuthenticated: boolean
  login: (u: string, p: string, companyId?: number) => Promise<void>
  logout: () => void
  switchCompany: (companyId: number) => Promise<void>
  /** Re-read who I am -- after creating a company, so the switcher
   *  shows it without a page reload. */
  refreshMe: () => Promise<void>
  can: (permission: string) => boolean
}

const Ctx = createContext<AuthValue | null>(null)

/** Log out after this long with no interaction. */
const IDLE_MS = 30 * 60 * 1000

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  // Restore an existing session on page refresh.
  useEffect(() => {
    let cancelled = false
    async function boot() {
      if (!getAccessToken()) { setLoading(false); return }
      try {
        const data = await fetchMe()
        if (!cancelled) setMe(data)
      } catch {
        clearTokens()
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void boot()
    return () => { cancelled = true }
  }, [])

  // React to token loss from anywhere (e.g. a failed refresh).
  useEffect(() => onAuthChange((authed) => {
    if (!authed) { setMe(null) }
  }), [])

  const logout = useCallback(() => {
    apiLogout()
    // Nothing one user fetched may still be in the cache for the next one
    // to sign in on the same machine.
    queryClient.clear()
    setMe(null)
  }, [])

  // Idle timeout. Accounting screens are often left open on a shared
  // office PC; an unattended session is a real risk, not a theoretical one.
  useEffect(() => {
    if (!me) return
    let timer: number
    const reset = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(logout, IDLE_MS)
    }
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }))
    reset()
    return () => {
      window.clearTimeout(timer)
      events.forEach((e) => window.removeEventListener(e, reset))
    }
  }, [me, logout])

  const login = useCallback(async (u: string, p: string, companyId?: number) => {
    await apiLogin(u, p, companyId)
    setMe(await fetchMe())
  }, [])

  /**
   * The company is baked into the signed token, so switching means asking
   * the server for a new one.
   *
   * This used to replay the password through /login, which meant the
   * browser had to keep the password in memory for the whole session --
   * and a session restored from a refresh token on page reload had none,
   * so the switcher silently did nothing. Reported as "the Demo company
   * cannot be selected" after a deploy, because a deploy is what makes
   * people reload.
   */
  const switchCompany = useCallback(async (companyId: number) => {
    await switchCompanyApi(companyId)
    // Query keys carry dates and ledger codes but not the company, so a
    // cached trial balance would be served straight back under the new
    // company. Emptied once the new token is in place, so anything that
    // refetches in the gap fetches as the new company.
    queryClient.clear()
    setMe(await fetchMe())
  }, [])

  const refreshMe = useCallback(async () => {
    setMe(await fetchMe())
  }, [])

  const can = useCallback(
    (permission: string) => !!me?.permissions.includes(permission), [me])

  const value = useMemo<AuthValue>(() => ({
    me, loading, isAuthenticated: !!me, login, logout, switchCompany,
    refreshMe, can,
  }), [me, loading, login, logout, switchCompany, refreshMe, can])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>')
  return v
}
